<template>
  <div class="billing-settings">
    <div class="bs-header">
      <h1>Fizetési beállítások</h1>
      <p>Csoportonkénti alapárak és adminisztrátori kedvezményszabályok</p>
    </div>

    <div v-if="error" class="error-message">{{ error }}</div>

    <!-- Group prices -->
    <div class="section">
      <div class="section-header"><h2>Csoportonkénti árak (HUF)</h2></div>
      <div class="table-wrapper">
        <table class="price-table">
          <thead>
            <tr><th>Csoport</th><th>Óra ára</th><th>Havi ár</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="g in groups" :key="g.id">
              <td>{{ g.name }}</td>
              <td><input type="number" v-model="priceInputs[g.id].per_class" @change="savePrice(g.id)" /></td>
              <td><input type="number" v-model="priceInputs[g.id].monthly" @change="savePrice(g.id)" /></td>
              <td><button class="btn-primary" @click="savePrice(g.id)">Mentés</button></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="section-hint">Tipp: A többcsoportos táncosok egyedi havi díjait a <router-link to="/settings/discounts" style="color: var(--button-primary); text-decoration: underline;">Kedvezmények</router-link> menüpontban tudod kézzel beállítani.</p>
    </div>

    <!-- Discount rules -->
    <div class="section">
      <div class="section-header">
        <h2>Kedvezményszabályok</h2>
        <button class="btn-primary" @click="openRuleModal">+ Szabály</button>
      </div>
      <div v-for="r in rules" :key="r.id" class="rule-card">
        <div class="rule-head">
          <strong>{{ r.name }}</strong>
          <span class="rule-kind">{{ kindText(r.kind) }}</span>
          <span class="rule-scope">{{ scopeText(r.scope) }}</span>
          <span class="rule-value">{{ formatPrice(r.value) }} {{ r.applies_to }}</span>
          <div class="rule-actions">
            <button class="btn-edit" @click="editRule(r)">Szerk</button>
            <button class="btn-delete" @click="deleteRule(r.id)">Töröl</button>
          </div>
        </div>
        <div class="rule-desc">{{ r.description }}</div>
      </div>
      <div v-if="rules.length === 0" class="empty-note">Nincs szabály.</div>
    </div>

    <div v-if="showRuleModal" class="modal-overlay" @click="showRuleModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingRule ? 'Szabály szerkesztése' : 'Új kedvezményszabály' }}</h3>
          <button @click="showRuleModal = false" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Név</label>
            <input type="text" v-model="ruleForm.name" placeholder="pl. Versenyző kedvezmény" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Típus</label>
            <select v-model="ruleForm.kind" class="modern-select">
              <option value="percent">Százalék (%)</option>
              <option value="fixed">Fix összeg (HUF)</option>
              <option value="sibling">Testvér/többcsoport (HUF/csoport)</option>
            </select>
          </div>
          <div class="form-group">
            <label>Érték</label>
            <input type="number" step="0.01" v-model="ruleForm.value" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Érvényes</label>
            <select v-model="ruleForm.applies_to" class="modern-select">
              <option value="total">Teljes összegre</option>
              <option value="class">Csak óránkénti</option>
              <option value="monthly">Csak havi</option>
            </select>
          </div>
          <div class="form-group">
            <label>Hatókör</label>
            <select v-model="ruleForm.scope" class="modern-select">
              <option value="global">Általános</option>
              <option value="custom">Egyedi</option>
            </select>
          </div>
          <div class="form-group">
            <label>Prioritás (kisebb = előbb)</label>
            <input type="number" v-model="ruleForm.priority" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Leírás</label>
            <input type="text" v-model="ruleForm.description" class="modern-input" />
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="ruleForm.is_active" /> Aktív
            </label>
          </div>
          <div class="form-actions">
            <button class="btn-secondary" @click="showRuleModal = false">Mégse</button>
            <button class="btn-primary" @click="saveRule">Mentés</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BillingSettingsItem',
  data() {
    return {
      groups: [],
      priceInputs: {},
      rules: [],
      error: '',
      showRuleModal: false,
      editingRule: null,
      ruleForm: { name: '', kind: 'percent', value: 0, applies_to: 'total', scope: 'global', priority: 0, description: '', is_active: true }
    };
  },
  async mounted() {
    await this.loadGroups();
    await this.loadRules();
  },
  methods: {
    api() { return this.$BACKEND_HOST + '/api'; },
    async loadGroups() {
      try {
        const r = await axios.get(`${this.api()}/settings/groups/info`);
        this.groups = r.data.groups || r.data || [];
        await this.loadPrices();
      } catch (e) { this.error = 'Hiba a csoportok betöltésekor'; }
    },
    async loadPrices() {
      try {
        const r = await axios.get(`${this.api()}/billing/group-prices`);
        const map = {};
        (r.data.prices || []).forEach(p => { map[p.group_id] = p; });
        this.groups.forEach(g => {
          const p = map[g.id] || {};
          this.$set(this.priceInputs, g.id, { per_class: p.price_per_class || 0, monthly: p.price_monthly || 0 });
        });
      } catch (e) { this.error = 'Hiba az árak betöltésekor'; }
    },
    async savePrice(groupId) {
      const inp = this.priceInputs[groupId];
      try {
        await axios.put(`${this.api()}/billing/group-prices`, {
          group_id: groupId,
          price_per_class: parseFloat(inp.per_class) || 0,
          price_monthly: parseFloat(inp.monthly) || 0,
          sibling_factor: 1
        });
      } catch (e) { this.error = 'Hiba az ár mentésekor'; }
    },
    async loadRules() {
      try {
        const r = await axios.get(`${this.api()}/billing/discount-rules`);
        this.rules = r.data.rules || [];
      } catch (e) { this.error = 'Hiba a szabályok betöltésekor'; }
    },
    openRuleModal() {
      this.editingRule = null;
      this.ruleForm = { name: '', kind: 'percent', value: 0, applies_to: 'total', scope: 'global', priority: 0, description: '', is_active: true };
      this.showRuleModal = true;
    },
    editRule(r) {
      this.editingRule = r;
      this.ruleForm = { ...r };
      this.showRuleModal = true;
    },
    async saveRule() {
      const payload = { ...this.ruleForm, value: parseFloat(this.ruleForm.value) || 0, priority: parseInt(this.ruleForm.priority, 10) || 0 };
      try {
        if (this.editingRule) {
          await axios.put(`${this.api()}/billing/discount-rules`, { ...payload, id: this.editingRule.id });
        } else {
          await axios.post(`${this.api()}/billing/discount-rules`, payload);
        }
        this.showRuleModal = false;
        await this.loadRules();
      } catch (e) { this.error = 'Hiba a szabály mentésekor'; }
    },
    async deleteRule(id) {
      if (!confirm('Biztosan törlöd?')) return;
      await axios.delete(`${this.api()}/billing/discount-rules/${id}`);
      await this.loadRules();
    },
    formatPrice(v) {
      return Math.round(v || 0).toLocaleString('hu-HU');
    },
    kindText(k) { return { percent: 'Százalék', fixed: 'Fix', sibling: 'Testvér' }[k] || k; },
    scopeText(s) { return { global: 'Általános', competitor: 'Versenyző', multi_group: 'Többcsoport', custom: 'Egyedi' }[s] || s; }
  }
};
</script>

<style scoped>
.billing-settings { max-width: 960px; margin: 0 auto; padding: 1.5rem; }
.bs-header h1 { margin-bottom: 0.2rem; }
.bs-header p { color: var(--color-text-muted); margin-top: 0; }
.section { background: var(--card-bg); border: 1px solid var(--color-border); border-radius: 12px; padding: 1.2rem; margin-bottom: 1.2rem; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.price-table { width: 100%; border-collapse: collapse; }
.price-table th, .price-table td { padding: 0.4rem; border-bottom: 1px solid var(--border); text-align: left; }
.price-table input { width: 100px; padding: 0.4rem; border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg); color: var(--color-text); }
.competitor-row { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
.competitor-row input { width: 100px; padding: 0.4rem; border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg); color: var(--color-text); }
.section-hint { color: var(--color-text-muted); font-size: 0.85rem; margin: 0.6rem 0 0; }
.rule-card { border: 1px solid var(--border); border-radius: 8px; padding: 0.7rem; margin-bottom: 0.5rem; }
.rule-head { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
.rule-kind, .rule-scope { background: var(--button-primary); color: #fff; border-radius: 6px; padding: 0.1rem 0.5rem; font-size: 0.8rem; }
.rule-value { color: var(--color-text-muted); }
.rule-actions { margin-left: auto; display: flex; gap: 0.4rem; }
.rule-desc { color: var(--color-text-muted); font-size: 0.85rem; margin-top: 0.3rem; }
.empty-note { color: var(--color-text-muted); font-style: italic; }
.error-message { color: #e5484d; margin-bottom: 0.8rem; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.75); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; backdrop-filter: blur(4px); }
.modal-content { background: var(--card-bg); border-radius: 16px; box-shadow: var(--shadow-lg); max-width: 440px; width: 100%; max-height: 90vh; overflow-y: auto; border: 1px solid var(--border); }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1.5rem 2rem; border-bottom: 1px solid var(--border); background: var(--color-background-soft); }
.modal-header h3 { margin: 0; font-size: 1.25rem; font-weight: 600; color: var(--color-heading); }
.close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: var(--color-text-muted); width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 8px; transition: all 0.2s ease; }
.close-btn:hover { background: var(--color-background); color: var(--color-text); }
.modal-body { padding: 2rem; }
.form-group { display: flex; flex-direction: column; gap: 0.5rem; }
.form-group label { font-weight: 600; color: var(--color-heading); font-size: 0.95rem; }
.checkbox-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
.form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid var(--border); }
.btn-edit { color: var(--button-primary); border: none; background: transparent; cursor: pointer; }
.btn-delete { color: #e5484d; border: none; background: transparent; cursor: pointer; }

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}

@media (max-width: 768px) {
  .billing-settings { padding: 1rem 0.5rem; }
  .price-table th, .price-table td { padding: 0.35rem; font-size: 0.85rem; white-space: nowrap; }
  .price-table input { width: 80px; font-size: 0.85rem; }
  .rule-head { flex-direction: column; align-items: flex-start; gap: 0.3rem; }
  .rule-actions { margin-left: 0; }
}
</style>
